import InsightTurn from '@/services/insightTurn';
import type { BedrockMessage } from '@/utils/beemAgentTypes';
import { conversationsRepo } from '@/pages/Insights/data/conversationsRepo';
import type { Conversation } from '@/pages/Insights/data/conversationsRepo';
import { logger } from '@/utils/logger';
import { errorReply } from './insightReplies';

const ns = 'app.models.insightBackgroundTurn';

const POLL_MS = 5000;
// Longer than any turn runs: past it the turn is left alone, and the conversation keeps its
// question without an answer, as it did before this existed.
const GIVE_UP_AFTER_MS = 30 * 60 * 1000;

export type BackgroundTurnDeps = {
  getTurn: (id: string) => Promise<InsightTurn>;
  getMessages: (id: string) => Promise<BedrockMessage[]>;
  getConversation: (id: string) => Promise<Conversation | undefined>;
  saveConversation: (conversation: Conversation) => Promise<Conversation>;
  sleep: (ms: number) => Promise<void>;
  now: () => number;
};

const defaultDeps: BackgroundTurnDeps = {
  getTurn: (id) => InsightTurn.getById(id),
  getMessages: (id) => InsightTurn.getMessages(id),
  getConversation: (id) => conversationsRepo.get(id),
  saveConversation: (c) => conversationsRepo.upsert(c),
  sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  now: () => Date.now(),
};

export type BackgroundOutcome =
  | 'saved'
  | 'failed'
  | 'cancelled'
  | 'gave-up'
  | 'conversation-gone'
  | 'conversation-moved-on';

/**
 * Finishes a turn whose conversation the user has left, into that conversation.
 *
 * Leaving a conversation used to cancel its running turn, so a question asked just before
 * switching (sending another question from a project, say) was left with no answer (BEEM-2362,
 * QA 2360). The turn now runs to its end in the background, and its outcome is written into the
 * conversation it was asked in: the agent's messages when it completes, an error reply when it
 * fails. The conversation is only written while its last message is still the unanswered
 * question, so a conversation the user has since continued is never overwritten.
 */
export async function finishInBackground(
  turnId: string,
  conversationId: string,
  onSaved?: (conversation: Conversation) => void,
  deps: BackgroundTurnDeps = defaultDeps,
): Promise<BackgroundOutcome> {
  const started = deps.now();
  // Recursive rather than a loop, which the lint config forbids awaiting in; each call returns
  // before the next starts, so the stack does not grow.
  const poll = async (): Promise<InsightTurn | 'gave-up'> => {
    try {
      const polled = await deps.getTurn(turnId);
      const current = polled.isStale
        ? polled.withStatus('ERROR', 'The assistant stopped responding. Please try again.')
        : polled;
      if (current.isTerminal) return current;
    } catch (e) {
      // Transient: the next poll tries again.
      logger.warn({ label: `${ns}.poll`, message: e });
    }
    if (deps.now() - started > GIVE_UP_AFTER_MS) return 'gave-up';
    await deps.sleep(POLL_MS);
    return poll();
  };
  const turn = await poll();
  if (turn === 'gave-up') return 'gave-up';
  if (turn.status === 'CANCELLED') return 'cancelled';

  const conversation = await deps.getConversation(conversationId);
  if (!conversation || conversation.readOnly) return 'conversation-gone';
  if (conversation.messages[conversation.messages.length - 1]?.role !== 'user')
    return 'conversation-moved-on';

  if (turn.status === 'ERROR') {
    const saved = await deps.saveConversation({
      ...conversation,
      messages: [...conversation.messages, errorReply(turn.error || 'Unknown error')],
    });
    onSaved?.(saved);
    return 'failed';
  }

  // The agent's messages are the source of truth for the history, as for a turn in view.
  const messages = await deps.getMessages(turnId);
  const traces =
    turn.reasoningText || turn.memoryCount > 0
      ? {
          ...(conversation.traces ?? {}),
          [messages.length - 1]: {
            reasoningText: turn.reasoningText,
            memoryCount: turn.memoryCount,
          },
        }
      : conversation.traces;
  const saved = await deps.saveConversation({
    ...conversation,
    messages,
    traces,
    sessionId: turn.sessionId ?? conversation.sessionId,
  });
  onSaved?.(saved);
  return 'saved';
}
